import type {
  CartLineItem,
  CheckoutConfigurations,
  Customer,
  InitialCardPayload,
  Offer,
  PaymentKeys,
  Pricing,
  PaymentMethod,
  AvailablePaymentMethod,
} from '@/types/checkout';
import { PaymentMethodEnum } from '@/types/common';
import type { ExcludeOtherPaymentMethod } from '@/types/common';
import type { CheckoutItem } from '@/types/checkout';
import type { DrawerScreen } from '@/types/screens';
import type { SavedCard } from '@/types/checkout';
import { useCallback, useState } from 'react';
import type { PublisherData } from '@/types/boot';
import type { AddressDetails } from '@/types/context';
import type { OrderResponse } from '@/types/order';
import { initialPromoCodeState, type PromoCodeState } from '@/utils/promoCode';
import type { Template } from '@/types/template';

export const useCheckout = () => {
  const [dataIsReady, setDataIsReady] = useState<boolean>(false);
  const [paymentMethodsAvailable, setPaymentMethodsAvailable] = useState<AvailablePaymentMethod[]>([]);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState<PaymentMethodEnum>(PaymentMethodEnum.CREDIT_CARD);
  const [otherMethodSelected, setOtherMethodSelected] = useState<ExcludeOtherPaymentMethod>();
  const [purchasedItems, setPurchasedItems] = useState<CheckoutItem[]>([]);
  const [drawerScreen, setDrawerScreen] = useState<DrawerScreen>();
  const [selectedCard, setSelectedCard] = useState<SavedCard>();
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [savedPaypalAccount, setSavedPaypalAccount] = useState<SavedCard>();
  const [customer, setCustomer] = useState<Customer>();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentKeys, setPaymentKeys] = useState<PaymentKeys>({} as PaymentKeys);
  const [publisher, setPublisher] = useState<PublisherData>();
  const [pricing, setPricing] = useState<Pricing>();
  const [offer, setOffer] = useState<Offer>();
  const [offers, setOffers] = useState<CartLineItem[]>([]);
  const [initialCardPayload, setInitialCardPayload] = useState<InitialCardPayload>();
  const [viewer, setViewer] = useState<DrawerScreen>();
  const [addressDetails, setAddressDetails] = useState<AddressDetails>();
  const [checkoutConfiguration, setCheckoutConfigurations] = useState<CheckoutConfigurations>();
  const [redirectLink, setRedirectLink] = useState<string>();
  const [orderData, setOrderData] = useState<OrderResponse | null>(null);
  const [promoCodeBreakdown, updatePromoCodeBreakdown] = useState<PromoCodeState>(initialPromoCodeState);
  const [orderId, setOrderId] = useState<string | undefined>();
  const [template, setTemplate] = useState<Template | undefined>();
  // ACDEV-27914: snapshotted ONCE at boot by CheckoutContent. See
  // `didSessionStartWithoutValidTax` and `shouldUseWalletDerivedZipTax`.
  const [sessionStartedWithoutValidTax, setSessionStartedWithoutValidTax] = useState<boolean>(false);

  const handleRemoveCard = useCallback(
    (card: SavedCard) => {
      const updatedCards = savedCards.filter((savedCard) => {
        // For new cards, compare by id if pspCardIdentifier is not available
        if (card.isNew && savedCard.isNew) {
          return savedCard.id !== card.id;
        }
        return savedCard.pspCardIdentifier !== card.pspCardIdentifier;
      });
      setSavedCards(updatedCards);

      // Check if the removed card was the selected one
      const isSelectedCard =
        selectedCard &&
        ((card.isNew && selectedCard.isNew && selectedCard.id === card.id) ||
          (!card.isNew && selectedCard.pspCardIdentifier === card.pspCardIdentifier) ||
          selectedCard.id === card.id);

      if (isSelectedCard) {
        if (updatedCards.length > 0) {
          setSelectedCard(updatedCards[0]);
        } else {
          setSelectedCard(undefined);
        }
      }
    },
    [savedCards, selectedCard, setSavedCards, setSelectedCard]
  );

  return {
    dataIsReady,
    setDataIsReady,
    paymentMethodsAvailable,
    setPaymentMethodsAvailable,
    paymentMethodSelected,
    setPaymentMethodSelected,
    otherMethodSelected,
    setOtherMethodSelected,
    purchasedItems,
    setPurchasedItems,
    drawerScreen,
    setDrawerScreen,
    selectedCard,
    setSelectedCard,
    savedCards,
    setSavedCards,
    savedPaypalAccount,
    setSavedPaypalAccount,
    customer,
    setCustomer,
    paymentMethods,
    setPaymentMethods,
    paymentKeys,
    setPaymentKeys,
    publisher,
    setPublisher,
    pricing,
    setPricing,
    offer,
    setOffer,
    offers,
    setOffers,
    initialCardPayload,
    setInitialCardPayload,
    viewer,
    setViewer,
    addressDetails,
    setAddressDetails,
    checkoutConfiguration,
    setCheckoutConfigurations,
    handleRemoveCard,
    redirectLink,
    setRedirectLink,
    orderData,
    setOrderData,
    promoCodeBreakdown,
    updatePromoCodeBreakdown,
    orderId,
    setOrderId,
    template,
    setTemplate,
    sessionStartedWithoutValidTax,
    setSessionStartedWithoutValidTax,
  };
};
